import React, { PropsWithChildren } from 'react';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import { useAnalytics } from '@backstage/core-plugin-api';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { makeStyles } from '@material-ui/core';
import MapIcon from '@material-ui/icons/MyLocation';
import CropSquareIcon from '@material-ui/icons/CropSquare';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import PostAddIcon from '@material-ui/icons/PostAdd';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import CategoryIcon from '@material-ui/icons/Category';
import LinkIcon from '@material-ui/icons/Link';
import DescriptionIcon from '@material-ui/icons/Description';
import SchoolIcon from '@material-ui/icons/School';
import GroupIcon from '@material-ui/icons/People';

import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 70,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    transition: 'opacity 0.5s ease-in-out;',
    '&:hover': {
      opacity: '0.7',
    },
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const configApi = useApi(configApiRef);
  const appBaseUrl = configApi.getOptionalString('app.baseUrl');
  const analytics = useAnalytics();

  const openNewTab = (url: string) => {
    return (event: React.MouseEvent<Element, MouseEvent>) => {
      event.preventDefault();
      analytics.captureEvent('click', 'link', {
        attributes: {
          event_category: 'click',
          event_label: 'link',
          value: url,
        },
      });
      window.open(url, '_blank', 'noopener,noreferrer');
    };
  };

  const logout = () => {
    localStorage.clear();
    sessionStorage.setItem('manualflow', '1');
    window.location.reload();
  };

  return (
    <>
      <SidebarPage>
        <Sidebar>
          <SidebarLogo />
          <SidebarItem
            icon={SearchIcon}
            to="search"
            text="Search"
            onClick={() => {
              analytics.captureEvent('click', 'navigation', {
                attributes: {
                  event_category: 'click',
                  event_label: 'navigation',
                  value: 'search',
                },
              });
            }}
          />
          <SidebarDivider />
          <SidebarGroup label="Menu" icon={<MenuIcon />}>
            {/* Global nav, not org-specific */}
            <SidebarItem
              icon={CategoryIcon}
              to="catalog"
              text="Catalog"
              onClick={() => {
                analytics.captureEvent('click', 'navigation', {
                  attributes: {
                    event_category: 'click',
                    event_label: 'navigation',
                    value: 'catalog',
                  },
                });
              }}
            />
            <MyGroupsSidebarItem
              singularTitle="My Group"
              pluralTitle="My Groups"
              icon={GroupIcon}
            />
            <SidebarItem
              icon={DescriptionIcon}
              to="docs"
              text="Docs"
              onClick={() => {
                analytics.captureEvent('click', 'navigation', {
                  attributes: {
                    event_category: 'click',
                    event_label: 'navigation',
                    value: 'docs',
                  },
                });
              }}
            />
            <SidebarItem
              icon={CreateComponentIcon}
              to="create"
              text="Contribute..."
              onClick={() => {
                analytics.captureEvent('click', 'navigation', {
                  attributes: {
                    event_category: 'click',
                    event_label: 'navigation',
                    value: 'contribute',
                  },
                });
              }}
            />
            <SidebarItem icon={PostAddIcon} to="templates" text="Templates" />
            <SidebarScrollWrapper>
              <SidebarItem icon={MapIcon} to="tech-radar" text="Tech Radar" />
              <SidebarItem
                icon={SchoolIcon}
                to="tech-updates"
                text="Tech Updates"
                onClick={() => {
                  analytics.captureEvent('click', 'navigation', {
                    attributes: {
                      event_category: 'click',
                      event_label: 'navigation',
                      value: 'tech updates',
                    },
                  });
                }}
              />
            </SidebarScrollWrapper>
            <SidebarItem
              icon={CropSquareIcon}
              to="innersource/progress"
              text="InnerSource Progress"
              onClick={() => {
                window.gtag('event', 'user_click', {
                  event_category: 'click',
                  event_label: 'navigation',
                  value: 'innersource progress',
                });
              }}
            />
            {/* End global nav */}
            <SidebarDivider />
          </SidebarGroup>

          <SidebarSpace />
          <SidebarDivider />
          <SidebarGroup label="Quick Links" icon={<MenuIcon />}>
            <SidebarItem
              icon={LinkIcon}
              onClick={openNewTab(
                `${appBaseUrl}/docs/default/component/innersource-101`,
              )}
              text="InnerSource 101"
            />
            <SidebarItem
              icon={LinkIcon}
              onClick={openNewTab(
                `${appBaseUrl}/docs/default/component/global-backstage`,
              )}
              text="Backstage"
            />
            <SidebarItem
              icon={LinkIcon}
              onClick={() => {
                analytics.captureEvent('click', 'navigation', {
                  attributes: {
                    event_category: 'click',
                    event_label: 'navigation',
                    value: 'e-learning',
                  },
                });
              }}
              to="https://sites.google.com/newscorp.com/innersource/e-learning"
              text="E-Learning"
            />
            <SidebarItem
              icon={LinkIcon}
              onClick={openNewTab(
                `${appBaseUrl}/catalog?filters%5Bkind%5D=user&filters%5Bnamespace%5D=default&filters%5Buser%5D=all`,
              )}
              text="Community"
            />

            <SidebarDivider />
          </SidebarGroup>
          <SidebarGroup
            label="Settings"
            icon={<UserSettingsSignInAvatar />}
            onClick={() => {
              analytics.captureEvent('click', 'navigation', {
                attributes: {
                  event_category: 'click',
                  event_label: 'navigation',
                  value: 'settings',
                },
              });
            }}
            to="/settings"
          >
            <SidebarSettings />
            <SidebarItem icon={ExitToAppIcon} onClick={logout} text="Logout" />
          </SidebarGroup>
        </Sidebar>
        {children}
      </SidebarPage>
    </>
  );
};
